import { componentScope } from "harmaja"
import * as L from "lonna"
import { Board, Container } from "../../../common/src/domain"
import { Rect } from "../../../common/src/geometry"
import { installKeyboardShortcut } from "./keyboard-shortcuts"

// ponytail: Areas are frames. No new item type; this only adds navigation over existing containers.
export type FrameNavigator = ReturnType<typeof frameNavigator>

export function frameNavigator(board: L.Property<Board>, viewRect: L.Property<Rect>, zoomToFit: (r: Rect[]) => void) {
    const frames: L.Property<Container[]> = L.view(board, (b) =>
        Object.values(b.items)
            .filter((i): i is Container => i.type === "container" && !i.hidden)
            // reading order: rows (by y, with some tolerance), then x
            .sort((a, b) => (Math.abs(a.y - b.y) < Math.min(a.height, b.height) / 2 ? a.x - b.x : a.y - b.y)),
    )
    const current = L.atom<number>(-1)

    function currentIndex() {
        const fs = frames.get()
        const c = current.get()
        if (c >= 0 && c < fs.length) return c
        // otherwise: the first frame whose center is inside the view
        const vr = viewRect.get()
        return fs.findIndex((f) => {
            const cx = f.x + f.width / 2
            const cy = f.y + f.height / 2
            return cx >= vr.x && cx <= vr.x + vr.width && cy >= vr.y && cy <= vr.y + vr.height
        })
    }
    function goTo(i: number) {
        const fs = frames.get()
        if (!fs.length) return
        const idx = ((i % fs.length) + fs.length) % fs.length
        current.set(idx)
        zoomToFit([fs[idx]])
    }
    const next = () => goTo(currentIndex() + 1)
    const prev = () => goTo(currentIndex() <= 0 ? frames.get().length - 1 : currentIndex() - 1)

    installKeyboardShortcut((e) => e.key === "PageDown", next)
    installKeyboardShortcut((e) => e.key === "PageUp", prev)

    // Presentation mode: chrome hidden (CSS class on the board container), arrows/space step frames, Esc exits.
    // ponytail: presenting doesn't lock editing or follow viewers; it's a local view mode.
    const presenting = L.atom(false)
    function startPresenting() {
        if (!frames.get().length) return
        presenting.set(true)
        document.documentElement.requestFullscreen?.().catch(() => {})
        const c = currentIndex()
        goTo(c >= 0 ? c : 0)
    }
    function stopPresenting() {
        presenting.set(false)
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {})
    }
    const togglePresenting = () => (presenting.get() ? stopPresenting() : startPresenting())
    const whilePresenting = (keys: string[]) => (e: JSX.KeyboardEvent) => presenting.get() && keys.includes(e.key)
    installKeyboardShortcut(whilePresenting(["ArrowRight", "ArrowDown", " "]), next)
    installKeyboardShortcut(whilePresenting(["ArrowLeft", "ArrowUp"]), prev)
    installKeyboardShortcut(whilePresenting(["Escape"]), stopPresenting)
    installKeyboardShortcut(
        (e) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "p",
        togglePresenting,
    )
    L.fromEvent(document, "fullscreenchange")
        .pipe(L.applyScope(componentScope()))
        .forEach(() => {
            if (!document.fullscreenElement && presenting.get()) presenting.set(false)
        })

    return { frames, current, goTo, next, prev, presenting, startPresenting, stopPresenting }
}
